import { Descriptions, DescriptionsProps, Modal, Result } from "antd"
import { useEffect, useState } from "react"
import { SystemDescriptions } from "src/common/descriptions"
import { PayableContractDebit } from "src/common/models/payableContractDebit"
import { ServiceDebitContract } from "src/common/models/serviceContractDebit"
import { formatToCurrency, formatToDisplayDatetime } from "src/common/util"
import { DeleteContractDebitForm } from "./DeleteContractDebitForm"
import { QuestionOutlined } from '@ant-design/icons';
import { FormActionButtons } from "src/components/FormActionButtons"
import { useDispatch, useSelector } from "react-redux"
import { RootState } from "src/state/reducer"
import { serviceLoading } from "src/common/apiLoader/state/selection"
import { DeleteElementRequest } from "src/common/models/common"
import { deletePayableContractDebitApi } from "src/features/AccountPayable/state/action"
import { DELETE_PAYABLE_CONTRACT_DEBIT_API } from "src/features/AccountPayable/state/actionTypes"
import { deleteServiceContractDebitApi } from "src/features/ServiceContract/state/action"

interface Props {
    open: boolean
    onCancel: VoidFunction
    onAccept: VoidFunction
    contractDebit: PayableContractDebit | ServiceDebitContract
    type: "PAYABLE_CONTRACT" | "SERVICE_CONTRACT"
}

export interface ReduxProps {
    error?: any
    isDeletingContractDebit: boolean
    deleteContractDebitSuccess: boolean
    deleteServiceContractDebitSuccess: boolean
}

export const ContractDebitDelete = (props: Props) => {
    const dispatch = useDispatch()

    const [actionButtonClicked, setActionButtonClicked] = useState<boolean>(false)
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false)

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        error: state.serviceContract.error,
        isDeletingContractDebit: serviceLoading(state, [DELETE_PAYABLE_CONTRACT_DEBIT_API]),
        deleteContractDebitSuccess: state.accountPayables.deletePayableContractDebitSuccess,
        deleteServiceContractDebitSuccess: state.serviceContract.deleteServiceContractDebitSuccess,
    }))

    const createDescriptions = SystemDescriptions.PAGES.CONTRACT_PAYMENT.CREATE_PAYMENT
    const descriptions = SystemDescriptions.PAGES.PAYABLE_CONTRACT.DELTE_CONTRACT_DEBIT_RECORD

    const items: DescriptionsProps['items'] = [
        {
            key: 'DESCRIPTION',
            label: createDescriptions.DESCRIPTIONS.DESCRIPTION,
            children: props.contractDebit?.description,
        },
        {
            key: 'CREATED_AT',
            label: createDescriptions.DESCRIPTIONS.CREATED_AT,
            children: formatToDisplayDatetime(props.contractDebit?.createdAt),
        },
        {
            key: 'AMOUN',
            label: createDescriptions.DESCRIPTIONS.AMOUNT,
            children: formatToCurrency(props.contractDebit?.amount.toString() || '0'),
        },
        {
            key: 'AMOUNT_PAYED',
            label: createDescriptions.DESCRIPTIONS.AMOUNT_PAYED,
            children: formatToCurrency(props.contractDebit?.amountPayed.toString() || '0'),
        },
        {
            key: 'OUTSTANDING',
            label: createDescriptions.DESCRIPTIONS.OUTSTANDING,
            children: formatToCurrency((Number(props.contractDebit?.amount) - Number(props.contractDebit?.amountPayed)).toString()),
        },
    ];

    useEffect(() => {
        if (actionButtonClicked && (reduxProps.deleteContractDebitSuccess || reduxProps.deleteServiceContractDebitSuccess)) {
            props.onAccept()
        }
    }, [reduxProps.deleteContractDebitSuccess, reduxProps.deleteServiceContractDebitSuccess])

    const renderDebitData = () => {
        return <Descriptions items={items} />
    }

    const renderButtons = () => (
        <FormActionButtons
            cancelText={descriptions.CONFIRM_MODAL.CANCEL_BUTTON}
            actionText={descriptions.CONFIRM_MODAL.ACTION_BUTTON}
            onCancel={function (): void {
                setShowConfirmation(false)
            }}
            onAccept={() => {
                const payload: DeleteElementRequest = {
                    id: props.contractDebit.id!,
                    comment: '',
                }
                setActionButtonClicked(true)

                if (props.type === "PAYABLE_CONTRACT") {
                    dispatch(deletePayableContractDebitApi(payload.id))
                } else if (props.type === "SERVICE_CONTRACT") {
                    dispatch(deleteServiceContractDebitApi(payload.id))
                }
            }}
            actionLoading={reduxProps.isDeletingContractDebit}
        />
    )

    const renderConfirmation = () => {
        return <Result
            icon={<QuestionOutlined />}
            title={descriptions.CONFIRM_MODAL.TITLE}
            subTitle={descriptions.CONFIRM_MODAL.DESCRIPTION}
            extra={[
                renderButtons(),
            ]}
        />
    }

    const onFinish = (_: string) => {
        setShowConfirmation(true)
    }

    return (
        <Modal
            title={descriptions.TITLE}
            centered
            open={props.open}
            width={1000}
            onOk={props.onAccept}
            okText={descriptions.SAVE_BUTTON}
            cancelText={descriptions.CANCEL_BUTTON}
            footer={<></>}
            closable={false}
            zIndex={1200}
            getContainer={false}
            destroyOnClose={true}
        >
            {
                showConfirmation
                    ? renderConfirmation()
                    : <>
                        {renderDebitData()}
                        <hr />
                        <DeleteContractDebitForm
                            onFinish={onFinish}
                            onCancel={props.onCancel}
                        />
                    </>
            }
        </Modal>
    )
}
